import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HomeIcon,
  UsersIcon,
  CreditCardIcon,
  DocumentTextIcon,
  CogIcon,
  XMarkIcon,
  UserPlusIcon,
  EyeIcon,
  PlusIcon,
  ClockIcon,
  DocumentPlusIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';

const menuItems = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: HomeIcon,
  },
  {
    name: 'Customers',
    icon: UsersIcon,
    children: [
      { name: 'Add Customer', href: '/customers/add', icon: UserPlusIcon },
      { name: 'View Customers', href: '/customers/view', icon: EyeIcon },
    ],
  },
  {
    name: 'Transactions',
    icon: CreditCardIcon,
    children: [
      { name: 'Add Transaction', href: '/transactions/add', icon: PlusIcon },
      { name: 'View Transactions', href: '/transactions/view', icon: EyeIcon },
      { name: 'Transaction History', href: '/transactions/history', icon: ClockIcon },
    ],
  },
  {
    name: 'Invoice',
    icon: DocumentTextIcon,
    children: [
      { name: 'Create Invoice', href: '/invoices/create', icon: DocumentPlusIcon },
      { name: 'Invoice History', href: '/invoices/history', icon: ArchiveBoxIcon },
    ],
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: CogIcon,
  },
];

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = React.useState({});

  const toggleExpanded = (itemName) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  const isActive = (href) => {
    return location.pathname === href;
  };

  const isParentActive = (children) => {
    return children?.some(child => location.pathname === child.href);
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col flex-grow bg-white dark:bg-gray-800 pt-5 pb-4 overflow-y-auto border-r border-gray-200 dark:border-gray-700">
            <div className="flex items-center flex-shrink-0 px-4">
              <div className="flex items-center">
                <div className="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">CMS</span>
                </div>
                <span className="ml-2 text-lg font-semibold text-gray-900 dark:text-white">
                  Admin Panel
                </span>
              </div>
            </div>
            <nav className="mt-8 flex-1 px-2 space-y-1">
              {menuItems.map((item) => (
                <SidebarItem
                  key={item.name}
                  item={item}
                  isActive={isActive}
                  isParentActive={isParentActive}
                  expandedItems={expandedItems}
                  toggleExpanded={toggleExpanded}
                />
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">CMS</span>
                </div>
                <span className="ml-2 text-lg font-semibold text-gray-900 dark:text-white">
                  Admin Panel
                </span>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <nav className="mt-4 px-2 space-y-1">
              {menuItems.map((item) => (
                <SidebarItem
                  key={item.name}
                  item={item}
                  isActive={isActive}
                  isParentActive={isParentActive}
                  expandedItems={expandedItems}
                  toggleExpanded={toggleExpanded}
                  onClose={onClose}
                />
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const SidebarItem = ({ item, isActive, isParentActive, expandedItems, toggleExpanded, onClose }) => {
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedItems[item.name];
  const isItemActive = item.href ? isActive(item.href) : isParentActive(item.children);

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={() => toggleExpanded(item.name)}
          className={`w-full sidebar-item ${isItemActive ? 'active' : ''}`}
        >
          <item.icon className="mr-3 h-5 w-5" />
          <span className="flex-1 text-left">{item.name}</span>
          <motion.svg
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.2 }}
            className="ml-auto h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </motion.svg>
        </button>
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="ml-4 space-y-1 overflow-hidden"
            >
              {item.children.map((child) => (
                <Link
                  key={child.name}
                  to={child.href}
                  onClick={onClose}
                  className={`sidebar-item text-sm ${isActive(child.href) ? 'active' : ''}`}
                >
                  <child.icon className="mr-3 h-4 w-4" />
                  {child.name}
                </Link>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <Link
      to={item.href}
      onClick={onClose}
      className={`sidebar-item ${isItemActive ? 'active' : ''}`}
    >
      <item.icon className="mr-3 h-5 w-5" />
      {item.name}
    </Link>
  );
};

export default Sidebar;
